// Contour folder builder.
//
// Layout (top → bottom): Value, Opacity, (dev-only material controls),
// Generate/Clear square action tiles, and a Domain tab-strip footer. Spatial
// clipping is driven entirely by the Domain selector — a Box/Sphere domain
// clips the isosurface to its bounds in `generateNow()`; `Full` means no clip.
//
// The action tiles and the domain strip are raw DOM appended to the folder
// body, so they survive pane child disposal between rebuilds. They are
// cached on the folder (mirroring the Slices pane) and re-pinned to the body
// bottom on every rebuild.

import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import { buildActionTiles, syncVisibilityTile } from './actionTiles';
import { buildDomainStrip, renderDomainStrip, syncDomainSelected, type DomainStripRefs } from './domainStrip';
import { rangeFormat } from '../numberFormat';
import { bindSlider } from '../sliderTicks';
import { niceMagnitude } from '@ui/controls/autoScale';
import type { FieldScale } from '@ui/controls/autoScale';
import type { TPaneLike, TFolderLike, Refs } from '../types';

interface ContourCtx {
    params: Record<string, unknown>;
    grid: { xMin: number; xMax: number; yMin: number; yMax: number; zMin: number; zMax: number };
    getContourRange?: () => { min: number; max: number } & FieldScale;
    applyContourMetalness?: (v: number) => void;
    applyContourRoughness?: (v: number) => void;
    applyContourEnvIntensity?: (v: number) => void;
    applyContourDoubleSide?: (v: boolean) => void;
    applyContourDepthWrite?: (v: boolean) => void;
    developerMode?: boolean;
}

interface ContourCustomRefs {
    actions: HTMLDivElement;
    domain: DomainStripRefs;
    offRefresh: () => void;
}

// Cached on the folder so the raw-DOM footer (action tiles + domain strip) and
// the UI_PANE_REFRESH listener survive full rebuilds — the folder element is
// stable across `buildContourSection` calls; only pane children get
// disposed and recreated.
const CUSTOM_REFS_BY_FOLDER = new WeakMap<TFolderLike, ContourCustomRefs>();

export function buildContourSection(paneArg: unknown, refs: Refs, ctx: ContourCtx): Refs {
    const pane = paneArg as TPaneLike;
    const { params, getContourRange, applyContourMetalness, applyContourRoughness, applyContourEnvIntensity, applyContourDoubleSide, applyContourDepthWrite, developerMode } = ctx;
    let folder = refs['contourFolderRef'] as TFolderLike | undefined;
    if (!folder) {
        folder = pane.addFolder({ title: 'Contour', expanded: true });
        if (folder.element) folder.element.dataset['section'] = 'contour';
        refs['contourFolderRef'] = folder;
    } else {
        try { const children = Array.isArray(folder.children) ? folder.children.slice() : []; children.forEach((c) => { try { (c as TFolderLike).dispose?.(); } catch { /* swallow */ } }); } catch { /* swallow */ }
    }

    // Stale UI_PANE_REFRESH listener belongs to the prior build; drop it so the
    // new one owns the sync. The footer DOM (actions + domain) is preserved.
    const existingRefs = CUSTOM_REFS_BY_FOLDER.get(folder);
    if (existingRefs) {
        try { existingRefs.offRefresh(); } catch { /* swallow */ }
    }

    const range: { min: number; max: number } & FieldScale = (typeof getContourRange === 'function')
        ? getContourRange()
        : { min: -1, max: 1, kind: 'symlog', linthresh: 1, origin: 0 };
    // Scale is chosen from the field's value distribution (see chooseFieldScale):
    // linear for low dynamic range, `log` for a wide strictly-positive field, else
    // `symlog` with a linear band around 0 so a 1/r³ spike compresses into the
    // tail. Drag step is sized per kind (log relies on the slider's decade snap).
    // Only the custom slider reads scale/linthresh/origin; ?nativeslider stays linear.
    const valueStep = range.kind === 'linear'
        ? niceMagnitude((range.max - range.min) / 200)
        : range.kind === 'symlog'
            ? niceMagnitude(range.linthresh / 20)
            : 0;
    bindSlider(folder, params, 'contourValue', {
        label: 'Value', min: range.min, max: range.max,
        ...(valueStep > 0 ? { step: valueStep } : {}),
        format: rangeFormat(range.min, range.max),
        scale: range.kind, origin: range.origin,
        ...(range.kind === 'symlog' ? { linthresh: range.linthresh } : {}),
    }).on('change', (ev) => {
        store.dispatch(INTENTS.SET_CONTOUR_VALUE, { value: ev.value as number });
    });

    bindSlider(folder, params, 'contourOpacity', { label: 'Opacity', min: 0.0, max: 1.0, step: 0.01 }).on('change', (ev) => {
        store.dispatch(INTENTS.SET_CONTOUR_OPACITY, { value: ev.value as number });
    });

    if (developerMode) {
        bindSlider(folder, params, 'contourMetalness', { label: 'Metalness', min: 0.0, max: 1.0, step: 0.01 }).on('change', (ev) => {
            try { if (typeof applyContourMetalness === 'function') applyContourMetalness(ev.value as number); } catch { /* swallow */ }
        });
        bindSlider(folder, params, 'contourRoughness', { label: 'Roughness', min: 0.0, max: 1.0, step: 0.01 }).on('change', (ev) => {
            try { if (typeof applyContourRoughness === 'function') applyContourRoughness(ev.value as number); } catch { /* swallow */ }
        });
        bindSlider(folder, params, 'contourEnvIntensity', { label: 'Env Intensity', min: 0.0, max: 2.0, step: 0.01 }).on('change', (ev) => {
            try { if (typeof applyContourEnvIntensity === 'function') applyContourEnvIntensity(ev.value as number); } catch { /* swallow */ }
        });
        folder.addBinding(params, 'contourDoubleSide', { label: 'Double Sided' }).on('change', (ev) => {
            try { if (typeof applyContourDoubleSide === 'function') applyContourDoubleSide(!!ev.value); } catch { /* swallow */ }
        });
        folder.addBinding(params, 'contourDepthWrite', { label: 'Depth Write' }).on('change', (ev) => {
            try { if (typeof applyContourDepthWrite === 'function') applyContourDepthWrite(!!ev.value); } catch { /* swallow */ }
        });
        bindSlider(folder, params, 'contourRenderOrder', { label: 'Render Order', min: -10, max: 10, step: 1 }).on('change', (ev) => {
            store.dispatch(INTENTS.SET_CONTOUR_RENDER_ORDER, { value: ev.value as number });
        });
    }

    folder.addBlade?.({ view: 'separator' });

    // Footer DOM — action tiles + domain strip. Preserved across rebuilds via
    // the WeakMap; the section registry fires a 'contour' rebuild on selection
    // add/delete/relabel, so we just re-render the domain tiles.
    const folderBodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (folderBodyEl) {
        // Defensive: drop any orphan footer DOM that isn't the tracked one
        // (folder identity change via HMR / manual refs reset would otherwise
        // accumulate duplicates).
        folderBodyEl.querySelectorAll<HTMLElement>('.pane-actions, .domain-strip').forEach((orphan) => {
            if (orphan !== existingRefs?.actions && orphan !== existingRefs?.domain.container) orphan.remove();
        });
    }
    const actions = existingRefs?.actions ?? buildActionTiles([
        { icon: 'fa-plus', label: 'Add', onClick: () => store.dispatch(INTENTS.GENERATE_CONTOUR) },
        { cls: 'is-clear', icon: 'fa-trash', label: 'Clear', onClick: () => store.dispatch(INTENTS.CLEAR_CONTOURS) },
        { id: 'visibility', icon: 'fa-eye-slash', label: 'Hide', onClick: () => store.dispatch(INTENTS.SET_CONTOUR_VISIBILITY, { visible: !params['showContours'] }) },
    ]);
    syncVisibilityTile(actions, 'visibility', !!params['showContours'], 'contours');
    let domain = existingRefs?.domain;
    if (!domain) {
        domain = buildDomainStrip('contour');
    } else {
        renderDomainStrip(domain.stripEl, 'contour');
    }

    const offRefresh = store.onIntent(INTENTS.UI_PANE_REFRESH, () => {
        syncDomainSelected('contour');
        syncVisibilityTile(actions, 'visibility', !!params['showContours'], 'contours');
    });
    CUSTOM_REFS_BY_FOLDER.set(folder, { actions, domain, offRefresh });

    // Pin the footer to the bottom of the folder body. `appendChild` is a
    // move-or-insert: on rebuilds these already live in the body and get moved
    // past the freshly-mounted pane children. Domain strip last (bottom).
    const bodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (bodyEl) {
        bodyEl.appendChild(actions);
        bodyEl.appendChild(domain.container);
    }

    if (pane && pane.refresh) pane.refresh();
    return refs;
}
